/**
 * useDraftConfig — local draft state for plugin ConfigPanel editing.
 *
 * Problem: Writing config back to the markdown document on every keystroke
 * causes a full CodeMirror document replacement, breaking IME composition
 * and scrolling to top.
 *
 * Solution: Maintain a local "draft" config that is independent of the
 * document. Changes are only flushed to the document when the user
 * explicitly clicks Run or Apply.
 */

import { OutputSerializer } from "@magic-md/core";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    resolveSerializerTargetLocator,
    type EditorSerializerTargetLocator,
    type SerializerTargetSourceRange,
} from "../lib/unit-edit-target";
import { useEditorStore } from "../store/editor-store";

const outputSerializer = new OutputSerializer();

export interface DraftConfigState {
    /** The current draft config (local, not persisted) */
    draftConfig: Record<string, unknown>;
    /** Whether the draft differs from the persisted config */
    isDirty: boolean;
    /** Update the draft config locally (no document write) */
    updateDraft: (value: unknown) => void;
    /** Flush draft config to the markdown document */
    applyDraft: () => void;
    /** Mark the current draft as applied without changing the draft value */
    markClean: () => void;
    /** Discard draft and revert to persisted config */
    discardDraft: () => void;
}

/**
 * Hook that manages a local draft of plugin config.
 *
 * @param definitionId - The definition ID to track
 * @param persistedConfig - The current config from `definition.config`
 * @param options - Optional: sourceConfig for use-derived definitions (to compute patch)
 */
export function useDraftConfig(
    definitionId: string | undefined,
    persistedConfig: Record<string, unknown> | undefined,
    options?: {
        /** Physical source unit kind. */
        unitKind?: "define" | "use";
        sourceConfig?: Record<string, unknown>;
        /** Current bind mappings from the ::use block (persisted) */
        bind?: Record<string, string>;
        /** Current with params from the ::use block (persisted) */
        with?: Record<string, unknown>;
        /** Exact CodeMirror target when the editor opened this config surface. */
        locator?: EditorSerializerTargetLocator;
        /** Parser range used when the surface was not opened from CodeMirror. */
        sourceRange?: SerializerTargetSourceRange;
    },
): DraftConfigState {
    const [draftConfig, setDraftConfig] = useState<Record<string, unknown>>(
        () => persistedConfig ?? {},
    );
    const [isDirty, setIsDirty] = useState(false);

    const targetKey = [
        definitionId ?? "",
        options?.unitKind ?? (options?.sourceConfig ? "use" : "define"),
        options?.locator?.startLine ?? "",
        options?.locator?.endLine ?? "",
        options?.locator?.occurrence ?? "",
        options?.locator?.fingerprint ?? "",
        options?.locator?.candidateCount ?? "",
        options?.sourceRange?.start ?? "",
        options?.sourceRange?.end ?? "",
    ].join(":");

    // Track the concrete unit occurrence, not only its possibly duplicated ID.
    const lastTargetKey = useRef(targetKey);

    // Track previous persisted config JSON to detect actual external changes.
    // This prevents the sync-back effect from reverting the draft to a stale
    // persistedConfig when isDirty transitions to false (e.g. after applyDraft).
    // For ::use blocks, persistedConfig is the engine-materialized config which
    // updates asynchronously — without this guard, the draft would revert to the
    // old materialized value before the engine re-materializes.
    //
    // P3 #11 review: this ref IS the JSON-compare implementation referenced by
    // the use-block protection. The deps `[persistedConfig, isDirty]` cause the
    // sync-back effect to re-run whenever isDirty flips false, even if the
    // engine has not yet re-materialized; the JSON snapshot in this ref is the
    // only mechanism that distinguishes "persisted truly changed" from
    // "isDirty just toggled". Removing it would re-introduce the stale-revert
    // bug. Keep.
    const prevPersistedJsonRef = useRef<string | undefined>(undefined);

    // Reset draft when switching to a different definition
    useEffect(() => {
        if (targetKey !== lastTargetKey.current) {
            lastTargetKey.current = targetKey;
            setDraftConfig(persistedConfig ?? {});
            setIsDirty(false);
            prevPersistedJsonRef.current = persistedConfig
                ? JSON.stringify(persistedConfig)
                : undefined;
        }
    }, [targetKey, persistedConfig]);

    // When persisted config changes externally (e.g. via raw editor in CodeMirror)
    // and we have no local edits, sync the draft.
    // Only sync when persistedConfig actually changes (JSON comparison), not merely
    // when isDirty transitions — this avoids reverting to a stale engine-materialized
    // config for ::use blocks after applyDraft.
    useEffect(() => {
        const json = persistedConfig ? JSON.stringify(persistedConfig) : undefined;
        if (!isDirty && persistedConfig && json !== prevPersistedJsonRef.current) {
            setDraftConfig(persistedConfig);
        }
        prevPersistedJsonRef.current = json;
    }, [persistedConfig, isDirty]);

    const isSwitchingDefinition = targetKey !== lastTargetKey.current;
    const effectiveDraftConfig = isSwitchingDefinition
        ? (persistedConfig ?? {})
        : draftConfig;
    const effectiveIsDirty = isSwitchingDefinition ? false : isDirty;

    const updateDraft = useCallback((value: unknown) => {
        const newConfig =
            typeof value === "object" && value !== null
                ? (value as Record<string, unknown>)
                : {};
        setDraftConfig(newConfig);
        setIsDirty(true);
    }, []);

    const applyDraft = useCallback(() => {
        if (!definitionId) return;

        const editorState = useEditorStore.getState();
        const activeTab = editorState.getActiveTab();
        if (!activeTab) return;

        let updated: string;

        // Check if this is a use-derived definition — write patch instead of full config
        const sourceConfig = options?.sourceConfig;
        const unitKind = options?.unitKind ?? (sourceConfig ? "use" : "define");
        const locator = resolveSerializerTargetLocator({
            content: activeTab.content,
            unitKind,
            unitId: definitionId,
            locator: options?.locator,
            sourceRange: options?.sourceRange,
            fallbackOccurrence: unitKind === "use" ? "first" : "last",
        });
        if (options?.locator?.fingerprint && !locator) {
            // The selected physical block changed or became ambiguous. Never
            // degrade to an ID-only serializer write.
            return;
        }
        if (unitKind === "use") {
            // Compute patch: only include fields that differ from the source config
            const patch: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(effectiveDraftConfig)) {
                if (
                    !sourceConfig
                    || JSON.stringify(value) !== JSON.stringify(sourceConfig[key])
                ) {
                    patch[key] = value;
                }
            }
            // Also include fields removed from source (set to undefined/null)
            // This is rare but ensures correctness
            updated = outputSerializer.updateUseConfig(
                activeTab.content,
                definitionId,
                patch,
                {
                    bind: options?.bind,
                    with: options?.with,
                },
                locator,
            );
        } else {
            updated = outputSerializer.updateConfig(
                activeTab.content,
                definitionId,
                effectiveDraftConfig,
                locator,
            );
        }
        if (updated !== activeTab.content) {
            // Use immediate parse so definitions are available synchronously
            // for the subsequent Run call (no 150ms debounce delay).
            editorState.setContentImmediate(updated, activeTab.id);
        }
        setIsDirty(false);
    }, [
        definitionId,
        effectiveDraftConfig,
        options?.unitKind,
        options?.sourceConfig,
        options?.bind,
        options?.with,
        options?.locator,
        options?.sourceRange,
    ]);

    const markClean = useCallback(() => {
        setIsDirty(false);
    }, []);

    const discardDraft = useCallback(() => {
        setDraftConfig(persistedConfig ?? {});
        setIsDirty(false);
    }, [persistedConfig]);

    return {
        draftConfig: effectiveDraftConfig,
        isDirty: effectiveIsDirty,
        updateDraft,
        applyDraft,
        markClean,
        discardDraft,
    };
}
